import { useMemo } from "react";

import type { ListingTypeOptions } from "@/utils/productTypes";
import { Models } from "api/types";
import { getDeterministicDiscountedPrice } from "utils";

export interface PriceDisplayStateInput {
  /** The base price in cents */
  basePriceInCents: number;
  /** The member price in cents (optional) */
  memberPriceInCents?: number | null;
  /** Discount data from the shop */
  discountData?: Models<"ShopProductDiscountModel"> | null;
  /** Whether the user is in guest mode (hides prices) */
  isGuest?: boolean;
  /** Whether there's a price range (e.g., "$50 - $100") */
  hasPriceRange?: boolean;
  /** Whether the item has linear pricing (for treatments) */
  hasLinearPricing?: boolean;
  /** The type of listing */
  type?: ListingTypeOptions;
}

export interface PriceDisplayState {
  /** The discounted price in cents, or null if no discount applies */
  discountedPrice: number | null;
  /** Whether to show strikethrough pricing */
  hasStrikethroughPrice: boolean;
  /** Whether to show "From:" prefix (multiple price options exist) */
  hasMultiplePriceOptions: boolean;
  /** The effective price to use for comparisons (discounted if available, otherwise base) */
  effectivePrice: number;
  /** Whether to show the member price */
  shouldShowMemberPrice: boolean;
  /** The member price in cents, or null if not available */
  memberPrice: number | null;
}

/**
 * Hook that calculates price display state for products, packages, and treatments.
 * Centralizes the logic for determining strikethrough pricing, "From:" prefix,
 * and member price visibility.
 *
 * @param input - The pricing input parameters
 * @returns The calculated price display state
 */
export const usePriceDisplayState = (input: PriceDisplayStateInput): PriceDisplayState => {
  const {
    basePriceInCents,
    memberPriceInCents,
    discountData,
    isGuest = false,
    hasPriceRange = false,
    hasLinearPricing = false,
    type,
  } = input;

  return useMemo(() => {
    // Calculate discounted price using shared utility
    const discountedPrice = getDeterministicDiscountedPrice(basePriceInCents, discountData);

    // Show strikethrough if not guest, discount exists, and is less than base price
    const hasStrikethroughPrice =
      !isGuest && discountedPrice !== null && discountedPrice < basePriceInCents;

    // "From" should only appear if there are multiple options/prices
    // This is true when there's a priceRange OR hasLinearPricing is false for treatments
    const hasMultiplePriceOptions = hasPriceRange || (!hasLinearPricing && type === "Treatment");

    // Effective price is the discounted price if available, otherwise the base price
    const effectivePrice =
      hasStrikethroughPrice && discountedPrice != null ? discountedPrice : basePriceInCents;

    // Normalize member price
    const memberPrice = memberPriceInCents ?? null;

    // Only show member price if it's cheaper than the effective price
    // and there's no price range (which would make comparison unclear)
    const shouldShowMemberPrice =
      memberPrice != null && memberPrice < effectivePrice && !hasPriceRange;

    return {
      discountedPrice,
      hasStrikethroughPrice,
      hasMultiplePriceOptions,
      effectivePrice,
      shouldShowMemberPrice,
      memberPrice,
    };
  }, [
    basePriceInCents,
    memberPriceInCents,
    discountData,
    isGuest,
    hasPriceRange,
    hasLinearPricing,
    type,
  ]);
};
